export const HIJRI_MONTHS = [
    'Muharram',
    'Safar',
    'Rabiul Awal',
    'Rabiul Akhir',
    'Jumadil Awal',
    'Jumadil Akhir',
    'Rajab',
    'Sya\'ban',
    'Ramadhan',
    'Syawal',
    'Dzulqa\'dah',
    'Dzulhijjah'
] as const;

export interface HijriDateInfo {
    day: number;
    month: number; // 1 - 12
    monthName: string;
    year: number;
    formatted: string; // contoh: "2 Jumadil Awal 1448 H"
}

/**
 * Mengonversi tanggal Masehi ke Hijriah yang konsisten di semua browser (Desktop & HP).
 * Menghindari bug ICU bawaan mobile browser di mana nama bulan Islam diterjemahkan
 * secara keliru menjadi nama bulan Masehi (misal bulan ke-5 Jumadil Awal menjadi 'Mei').
 */
export function getHijriDate(date: Date = new Date()): HijriDateInfo {
    try {
        // Minta komponen angka (numeric) dari Intl agar tidak terkena bug translasi nama bulan di mobile browser
        const formatter = new Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric'
        });
        const parts = formatter.formatToParts(date);
        let day: number | null = null;
        let month: number | null = null;
        let year: number | null = null;

        for (const p of parts) {
            if (p.type === 'day') day = parseInt(p.value, 10);
            if (p.type === 'month') month = parseInt(p.value, 10);
            if (p.type === 'year') year = parseInt(p.value, 10);
        }

        if (day && month && year && month >= 1 && month <= 12 && year > 1400) {
            const monthName = HIJRI_MONTHS[month - 1];
            return {
                day,
                month,
                monthName,
                year,
                formatted: `${day} ${monthName} ${year} H`
            };
        }
    } catch {
        // Fallback jika browser tidak mendukung kalender islamic-umalqura
    }

    // Algoritma astronomis (Kuwaiti algorithm) sebagai fallback yang 100% konsisten
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const dNum = date.getDate();
    const a = Math.floor((14 - m) / 12);
    const yPrime = y + 4800 - a;
    const mPrime = m + 12 * a - 3;
    const jdn = dNum + Math.floor((153 * mPrime + 2) / 5) + 365 * yPrime + Math.floor(yPrime / 4) - Math.floor(yPrime / 100) + Math.floor(yPrime / 400) - 32045;
    const l = jdn - 1948440 + 10632;
    const n = Math.floor((l - 1) / 10631);
    const lPrime = l - 10631 * n + 354;
    const j = (Math.floor((10985 - lPrime) / 5316)) * (Math.floor((50 * lPrime) / 17719)) + (Math.floor(lPrime / 5670)) * (Math.floor((43 * lPrime) / 15238));
    const lDoublePrime = lPrime - (Math.floor((30 - j) / 15)) * (Math.floor((17719 * j) / 50)) - (Math.floor(j / 16)) * (Math.floor((15238 * j) / 43)) + 29;
    const hMonth = Math.floor((24 * lDoublePrime) / 709);
    const hDay = lDoublePrime - Math.floor((709 * hMonth) / 24);
    const hYear = 30 * n + j - 30;
    const safeMonth = Math.max(1, Math.min(12, hMonth));
    const monthName = HIJRI_MONTHS[safeMonth - 1];

    return {
        day: hDay,
        month: safeMonth,
        monthName,
        year: hYear,
        formatted: `${hDay} ${monthName} ${hYear} H`
    };
}
